import { AssistantUITools } from "@repo/api/client";
import { Button, Flex, Icon, Text } from "@repo/ui";
import { ToolUIPart } from "ai";
import { useRef } from "react";
import {
	ConfirmToolCallArg,
	type ConfirmToolCallFn,
} from "../../../ai/useAssistantChat";
import { useEntity } from "../../../db/DatabaseProvider";
import { TaskId } from "../../../db/schema";
import { TextEditor, type TextEditorRef } from "../../editor/TextEditor";
import { ToolCallView } from "./ToolCallView";

type AssistantToolUIPart<ToolName extends keyof AssistantUITools> = ToolUIPart<
	Pick<AssistantUITools, ToolName>
>;

export const SendMessageToPatientToolCallView: React.FC<{
	part: AssistantToolUIPart<"send_message_to_patient">;
	isPendingConfirmation: boolean;
	confirmToolCall: ConfirmToolCallFn;
	taskId?: TaskId;
}> = ({ part, isPendingConfirmation, confirmToolCall, taskId }) => {
	const task = useEntity("task", taskId);
	const patient = useEntity("patient", task?.patient);

	// Handle pending confirmation state
	if (isPendingConfirmation) {
		if (part.state !== "input-available") return null;

		return (
			<SendMessageToolCallConfirmation
				assistantMessage={part.input?.message}
				phoneNumber={patient?.phoneNumber}
				onConfirm={(arg) =>
					confirmToolCall<"send_message_to_patient">(part.toolCallId, arg)
				}
			/>
		);
	}

	// For all other states (loading, output-available, output-error), use generic tool view
	return <ToolCallView part={part} />;
};

const SendMessageToolCallConfirmation: React.FC<{
	assistantMessage?: string;
	phoneNumber?: string;
	onConfirm: (arg: ConfirmToolCallArg<"send_message_to_patient">) => void;
}> = ({ assistantMessage, phoneNumber, onConfirm }) => {
	const inputRef = useRef<TextEditorRef>(null);

	const originalMessage = assistantMessage || "";

	const handleApprove = () => {
		const editedMessage = inputRef.current?.getValue();
		const finalMessage = editedMessage?.trim();

		console.log("finalMessage", finalMessage);

		if (!finalMessage) {
			onConfirm({
				permission: "reject",
				output: {
					success: false,
					code: "INPUT_VALIDATION_ERROR",
					message: "Message cannot be empty",
				},
			});
			return;
		}

		onConfirm({
			permission: "approve",
			input: { message: finalMessage },
		});
	};

	const handleReject = () => {
		onConfirm({
			permission: "reject",
			output: {
				success: false,
				code: "REJECTED_BY_USER",
				message: "Cancelled sending message",
			},
		});
	};

	return (
		<Flex
			column
			gap="md"
			padding="md"
			borderRadius="md"
			background="blue2"
			borderWidth="md"
			borderColor="blue6"
			flex
		>
			{/* Header */}
			<Flex align="flex-start" gap="xs">
				<Flex shrink={0} padding="xxxs">
					<Icon name="AlertCircle" size="sm" color="blue9" />
				</Flex>
				<Text size="md" color="blue12" weight="medium">
					Confirm message to patient
				</Text>
			</Flex>

			{/* Patient phone number */}
			{phoneNumber && (
				<Flex
					align="center"
					gap="xs"
					padding="xs"
					background="blue1"
					borderRadius="sm"
				>
					<Text size="md" color="blue11">
						Sending to {phoneNumber}
					</Text>
				</Flex>
			)}

			{/* Message content */}
			<Flex column gap="xs">
				<Flex
					column
					gap="xs"
					padding="xs"
					borderRadius="sm"
					background="background"
					borderWidth="md"
					borderColor="blue4"
				>
					<TextEditor
						ref={inputRef}
						placeholder="Edit message..."
						initialValue={originalMessage}
					/>
				</Flex>
			</Flex>

			{/* Actions */}
			<Flex gap="xs" justify="space-between" align="center">
				<Button size="sm" variant="ghost" onPress={handleReject}>
					<Flex align="center" gap="xs">
						<Icon name="X" size="xs" />
						<Text size="md">Cancel</Text>
					</Flex>
				</Button>
				<Button size="sm" variant="primary" onPress={handleApprove}>
					<Flex align="center" gap="xs">
						<Icon name="ArrowUp" size="xs" />
						<Text size="md">Send</Text>
					</Flex>
				</Button>
			</Flex>
		</Flex>
	);
};
